import { Box } from "@mui/material";
import React, { useMemo, useState } from "react";

import { ConfidenceSlider } from "features/confidenceSlider/ConfidenceSlider";
import { NewDetectorForm } from "hooks/CreateDetector/useCreateDetectorForm";
import { DetectorModes } from "types/createDetector";
import { LocalNotes } from "types/notes";

import { CreateBinary, CreateBinaryProps } from "../components/CreateBinary";
import { BoundingBoxes } from "../components/CreateBoundingBoxes";
import { CreateCount, CreateCountProps } from "../components/CreateCount";
import { CreateMultichoice } from "../components/CreateMultichoice";
import { CreateTextRecognition, CreateTextRecognitionProps } from "../components/CreateTextRecognition";
import { LocalNotesContent } from "../components/LocalNotesContent";

export interface DetectorGroup {
  id: string;
  name: string;
}

type ModeAnswerComponentProps = {
  queryValue: string;
  handleChangeValue: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  setBeforeCreationHook: (callback: VoidFunction | null) => void;
  onOptionsCountChange: (count: number) => void;
} & CreateBinaryProps &
  CreateCountProps &
  CreateTextRecognitionProps;

const MODE_ANSWER_COMPONENTS: Record<DetectorModes, React.ComponentType<ModeAnswerComponentProps>> = {
  [DetectorModes.BINARY]: CreateBinary,
  [DetectorModes.COUNT]: CreateCount,
  [DetectorModes.MULTICHOICE]: CreateMultichoice,
  [DetectorModes.TEXT_RECOGNITION]: CreateTextRecognition,
  [DetectorModes.BOUNDING_BOX]: BoundingBoxes,
};

export interface DetectorCreationSettings {
  MAX_COUNT_LIMIT?: number;
  MAX_LENGTH?: number;
}
export interface Step2Props {
  newDetectorForm: NewDetectorForm;
  handleConfidenceThresholdChange: (newValue: number) => void;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  notesData: LocalNotes;
  setNotesData: React.Dispatch<React.SetStateAction<LocalNotes>>;
  detectorCreationSettings: {
    MAX_COUNT_LIMIT: number;
  };
  setHasErrors?: (hasErrors: boolean) => void;
  setBeforeCreationHook: (callback: VoidFunction | null) => void;
}

export function Step2(props: Step2Props) {
  const {
    newDetectorForm,
    handleConfidenceThresholdChange,
    onInputChange,
    notesData,
    setNotesData,
    detectorCreationSettings,
    setHasErrors,
    setBeforeCreationHook,
  } = props;

  const { answerMode, maxCapCount, confidenceThreshold, labelPrompt, validBboxLabels } = newDetectorForm;
  const AnswerComponent = useMemo(() => MODE_ANSWER_COMPONENTS[answerMode], [answerMode]);

  const [multiChoiceOptions, setMultiChoiceOptions] = useState<number>(0);

  const handleSliderValueChange = (value: number) => {
    handleConfidenceThresholdChange(value);
  };

  return (
    <Box className="w-[95%] mx-auto">
      <Box className="flex flex-col space-y-3 my-8">
        <AnswerComponent
          queryValue={labelPrompt}
          handleChangeValue={onInputChange}
          setBeforeCreationHook={setBeforeCreationHook}
          countingObjectValue={validBboxLabels}
          maxCapCount={maxCapCount}
          onInputChange={onInputChange}
          setHasErrors={setHasErrors}
          detectorCreationSettings={detectorCreationSettings}
          handleConfidenceThresholdChange={handleConfidenceThresholdChange}
          newDetectorForm={newDetectorForm}
          onOptionsCountChange={(count: number) => setMultiChoiceOptions(count)}
        />
        {/* For COUNT/TEXT the confidence slider is displayed before the notes, so this will not be displayed here */}
        {![DetectorModes.COUNT, DetectorModes.TEXT_RECOGNITION, DetectorModes.BOUNDING_BOX].includes(answerMode) && (
          <ConfidenceSlider
            value={confidenceThreshold}
            setValue={handleSliderValueChange}
            setValueCommitted={handleSliderValueChange}
            defaultShowTradeoff
            hideTradeoffCharts={answerMode !== DetectorModes.BINARY}
            totalClasses={multiChoiceOptions}
          />
        )}

        <LocalNotesContent notesData={notesData} setNotesData={setNotesData} />
      </Box>
    </Box>
  );
}
